/**
 * RapierDebugRenderer - visualizes Rapier physics state using THREE.js lines. Holds a
 * LineSegments mesh receiving vertex/color buffers from Rapier world on each update.
 * The mesh is created on first use (enabled + non-empty debug geometry) and removed when disabled.
 */

import * as THREE from 'three';

import type * as RAPIER from '@dimforge/rapier3d-compat';

export class RapierDebugRenderer {
  mesh: THREE.LineSegments | null = null;
  world: RAPIER.World;
  enabled = false;

  private readonly scene: THREE.Scene;

  constructor(scene: THREE.Scene, world: RAPIER.World) {
    this.scene = scene;
    this.world = world;
  }

  update() {
    if (!this.enabled) {
      this.disposeMesh();
      return;
    }

    const { vertices, colors } = this.world.debugRender();

    if (vertices.length === 0) {
      if (this.mesh) {
        this.mesh.visible = false;
        this.mesh.geometry.setDrawRange(0, 0);
      }
      return;
    }

    if (!this.mesh) {
      // WebGPU: the mesh must never enter the scene with an empty BufferGeometry (no `position`).
      // Passes that apply a node override material (e.g. tone-mapping mask) compile TSL against this
      // drawable and warn or misbehave if `position` is missing — so the first create must use real
      // buffers from `debugRender()` before `scene.add`.
      const geometry = new THREE.BufferGeometry();
      const positionAttr = new THREE.BufferAttribute(vertices, 3);
      positionAttr.setUsage(THREE.DynamicDrawUsage);
      geometry.setAttribute('position', positionAttr);
      const colorAttr = new THREE.BufferAttribute(colors, 4);
      colorAttr.setUsage(THREE.DynamicDrawUsage);
      geometry.setAttribute('color', colorAttr);
      geometry.setDrawRange(0, vertices.length / positionAttr.itemSize);
      this.mesh = new THREE.LineSegments(
        geometry,
        new THREE.LineBasicMaterial({ color: 0xffffff, vertexColors: true })
      );
      this.mesh.frustumCulled = false;
      this.scene.add(this.mesh);
      return;
    }

    let positionAttr = this.mesh.geometry.getAttribute('position');
    let colorAttr = this.mesh.geometry.getAttribute('color');
    if (positionAttr && colorAttr && positionAttr.array.length >= vertices.length && colorAttr.array.length >= colors.length) {
      // Update the existing geometry buffers
      positionAttr.array.set(vertices);
      positionAttr.needsUpdate = true;
      colorAttr.array.set(colors);
      colorAttr.needsUpdate = true;
      this.mesh.geometry.setDrawRange(0, vertices.length / positionAttr.itemSize);
    } else {
      // Recreate the buffers to allocate more vertices
      if (positionAttr && positionAttr instanceof THREE.BufferAttribute) {
        positionAttr.dispose();
      }
      if (colorAttr && colorAttr instanceof THREE.BufferAttribute) {
        colorAttr.dispose();
      }
      positionAttr = new THREE.BufferAttribute(vertices, 3);
      positionAttr.setUsage(THREE.DynamicDrawUsage);
      this.mesh.geometry.setAttribute('position', positionAttr);
      colorAttr = new THREE.BufferAttribute(colors, 4);
      colorAttr.setUsage(THREE.DynamicDrawUsage);
      this.mesh.geometry.setAttribute('color', colorAttr);
      this.mesh.geometry.setDrawRange(0, vertices.length / positionAttr.itemSize);
    }
    this.mesh.visible = true;
  }

  dispose() {
    this.disposeMesh();
  }

  private disposeMesh(): void {
    if (!this.mesh) {
      return;
    }
    this.mesh.geometry.dispose();
    const mat = this.mesh.material;
    if (Array.isArray(mat)) {
      mat.forEach(m => m.dispose());
    } else {
      mat.dispose();
    }
    this.mesh.removeFromParent();
    this.mesh = null;
  }
}
